import { createSlice, PayloadAction, createSelector } from "@reduxjs/toolkit";
import { RootState } from "./index";
import { MarketplaceResponse } from "../types/shared";
import {
  ConsultPharmacyPair,
  PharmacistProfileResponse,
} from "../types/pharmacistProfile.types";
import { selectHasSupportedPharmacistCapability } from "./auth-slice";

// ITAM-2622 (3.4): the consultation pharmacy context.
//
// The manually provisioned pair returned by GET /pharmacist_profile/user_id/{id}
// is the SOLE consultation pharmacy context for both personas — no retail
// fallback, no frontend inference (decisions 14-16). Association changes during
// an active consult or its grace window are out of scope (decision 18), so this
// slice carries no per-consult snapshot, no removal lifecycle and no
// replacement handling: a normal load, a post-mutation refetch, and a retry for
// the exceptional missing-pair state.

export type ConsultPharmacyStatus =
  | "idle"
  | "loading"
  | "loaded"
  | "noProfile"
  | "error";

export type ConsultPharmacyDetailStatus =
  | "idle"
  | "loading"
  | "loaded"
  | "error";

export interface ConsultPharmacyContextState {
  profile: PharmacistProfileResponse | null;
  pair: ConsultPharmacyPair | null;
  status: ConsultPharmacyStatus;
  /** True after a fetch that FAILED (as opposed to one that found no profile). */
  loadError: boolean;
  detail: MarketplaceResponse | null;
  detailStatus: ConsultPharmacyDetailStatus;
}

const initialState: ConsultPharmacyContextState = {
  profile: null,
  pair: null,
  status: "idle",
  loadError: false,
  detail: null,
  detailStatus: "idle",
};

/** BF-4: the two keys are populated atomically — both or neither. */
const pairFrom = (
  profile: PharmacistProfileResponse | null
): ConsultPharmacyPair | null => {
  if (
    profile == null ||
    profile.marketplaceId == null ||
    profile.branchMarketplaceId == null
  ) {
    return null;
  }
  return {
    marketplaceId: profile.marketplaceId,
    branchMarketplaceId: profile.branchMarketplaceId,
  };
};

const consultPharmacyContextSlice = createSlice({
  name: "consultPharmacyContext",
  initialState,
  reducers: {
    fetchStart: (state) => {
      state.status = "loading";
      state.loadError = false;
    },
    /**
     * The ONLY success path, and it is exhaustive on purpose so a failure can
     * never masquerade as an absence:
     *  - a profile      -> adopt it, and adopt the pair (or clear it when the
     *                      keys are absent, which is the incomplete-provisioning
     *                      state of decision 17);
     *  - `null` (a 404) -> no profile row yet: clear both, status `noProfile`,
     *                      which is the onboarding state the page renders.
     */
    fetchSucceeded: (
      state,
      action: PayloadAction<PharmacistProfileResponse | null>
    ) => {
      const profile = action.payload;
      state.loadError = false;
      if (profile == null) {
        state.profile = null;
        state.pair = null;
        state.detail = null;
        state.detailStatus = "idle";
        state.status = "noProfile";
        return;
      }
      const nextPair = pairFrom(profile);
      // Drop stale enrichment when the pair itself changed or disappeared.
      if (
        nextPair == null ||
        state.pair == null ||
        nextPair.marketplaceId !== state.pair.marketplaceId ||
        nextPair.branchMarketplaceId !== state.pair.branchMarketplaceId
      ) {
        state.detail = null;
        state.detailStatus = "idle";
      }
      state.profile = profile;
      state.pair = nextPair;
      state.status = "loaded";
    },
    /**
     * Network, timeout, 5xx, 403 — anything that is NOT a 404. The last known
     * context is PRESERVED deliberately: a failed request is not evidence that
     * the association is gone, and treating it as such would disable activation
     * and break a live consult over a transient blip.
     */
    fetchFailed: (state) => {
      state.status = state.profile != null ? "loaded" : "error";
      state.loadError = true;
    },
    detailFetchStart: (state) => {
      state.detailStatus = "loading";
    },
    detailFetchSucceeded: (
      state,
      action: PayloadAction<MarketplaceResponse>
    ) => {
      state.detail = action.payload;
      state.detailStatus = "loaded";
    },
    detailFetchFailed: (state) => {
      state.detailStatus = "error";
    },
    /**
     * The `retry-on-demand` half of the enrichment contract (3.4). Enrichment is
     * fetched once per pair, so without this a single transient failure is
     * permanent for the session: `error` is otherwise only left when the pair
     * itself changes, and the pair does not change. That is not cosmetic —
     * `DeliverySection` takes the pharmacy coordinates and both marketplace ids
     * from this response, so delivery pricing stays unavailable until the whole
     * app is reloaded.
     *
     * Returning to `idle` (rather than fetching from here) is what keeps the
     * loader the single fetch site and makes an automatic failure loop
     * impossible: only an explicit demand — a user-initiated retry, or the tab
     * regaining focus — reaches this reducer.
     */
    detailRetryRequested: (state) => {
      if (state.detailStatus === "error") {
        state.detailStatus = "idle";
      }
    },
    reset: () => initialState,
  },
  extraReducers: (builder) => {
    // The context belongs to one authenticated persona and must not outlive it.
    builder.addCase("auth/logout", () => initialState);
    builder.addCase("auth/personaExit", () => initialState);
  },
});

export const consultPharmacyContextActions = consultPharmacyContextSlice.actions;

export const selectConsultPharmacyState = (state: RootState) =>
  state.consultPharmacyContext;

export const selectConsultPharmacyPair = (state: RootState) =>
  state.consultPharmacyContext.pair;

export const selectConsultPharmacyDetail = (state: RootState) =>
  state.consultPharmacyContext.detail;

export const selectPharmacistProfile = (state: RootState) =>
  state.consultPharmacyContext.profile;

/**
 * ONE predicate consumed by BOTH availability controls — "Aktiviraj dostupnost"
 * and the resume "Dostupan sam". Pause is never gated: going unavailable is
 * always safe.
 */
export const selectCanActivateAvailability = createSelector(
  [selectHasSupportedPharmacistCapability, selectConsultPharmacyPair],
  (hasCapability, pair) => hasCapability && pair != null
);

export default consultPharmacyContextSlice;
